'use client';

import React, { useEffect, useState, useRef, useCallback } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { fetchProducts } from '@/lib/api';
import { Search, X, Tag, ArrowRight, ChevronRight, Loader2 } from 'lucide-react';
import { getCategorySlugFromName } from '@/lib/category-slugs';

interface Product {
  id: string;
  name: string;
  category: string;
  images: string[];
  discountPercentage?: number;
  slug: string;
}

interface SearchModalProps {
  onClose: () => void;
}

// Popüler kategoriler
const popularCategories = [
  { name: 'Berber Koltukları', slug: 'berber-koltuklari' },
  { name: 'Kuaför Tezgahları', slug: 'kuafor-tezgahlari' },
  { name: 'Yıkama Setleri', slug: 'yikama-setleri' },
  { name: 'Çocuk Koltukları', slug: 'cocuk-koltuklari' },
];

const SearchModal: React.FC<SearchModalProps> = ({ onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const modalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();

  // Yerel depolamadan son aramaları yükle
  useEffect(() => {
    const savedSearches = localStorage.getItem('recentSearches');
    if (savedSearches) {
      try {
        const parsedSearches = JSON.parse(savedSearches);
        if (Array.isArray(parsedSearches)) {
          setRecentSearches(parsedSearches.slice(0, 5)); // Son 5 aramayı göster
        }
      } catch (e) {
        console.error('Son aramalar yüklenirken hata:', e);
      }
    }
  }, []);

  // Yeni aramayı kaydet
  const saveSearch = useCallback((term: string) => {
    if (!term.trim()) return;
    
    const newSearches = [
      term,
      ...recentSearches.filter(s => s !== term) // Tekrarları kaldır
    ].slice(0, 5); // Maksimum 5 arama tut
    
    setRecentSearches(newSearches);
    localStorage.setItem('recentSearches', JSON.stringify(newSearches));
  }, [recentSearches]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    
    // Input'a otomatik odaklan
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  useEffect(() => {
    const searchProducts = async () => {
    if (searchTerm.trim() === '') {
      setSearchResults([]);
      return;
    }

      setIsLoading(true);
      
      try {
        // API'den ürünleri ara
        const result = await fetchProducts(
          1, // sayfa
          6, // limit - ilk 6 sonucu göster
          undefined, // kategori yok
          undefined, // öne çıkan
          searchTerm // arama terimi
        );

        if (result.success) {
          setSearchResults(result.data.products);
        } else {
          setSearchResults([]);
        }
      } catch (error) {
        console.error('Arama hatası:', error);
        setSearchResults([]);
      } finally {
        setIsLoading(false);
      }
    };

    // Aramayı debounce et (300ms)
    const debounceTimeout = setTimeout(() => {
      searchProducts();
    }, 300);

    return () => clearTimeout(debounceTimeout);
  }, [searchTerm]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim() !== '') {
      saveSearch(searchTerm);
      router.push(`/urunler?search=${encodeURIComponent(searchTerm)}`);
      onClose();
    }
  };

  const handleClickOutside = (e: React.MouseEvent<HTMLDivElement>) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  const goToSearchResults = (term: string) => {
    saveSearch(term);
    router.push(`/urunler?search=${encodeURIComponent(term)}`);
    onClose();
  };

  const goToCategory = (slug: string) => {
    router.push(`/urunler/kategori/${slug}`);
    onClose();
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('recentSearches');
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm p-4 pt-16 md:pt-28 overflow-y-auto"
      onClick={handleClickOutside}
    >
      <div
        ref={modalRef}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl animate-fadeIn overflow-hidden border border-gray-100"
      >
        {/* Header */}
        <div className="px-5 py-4 flex justify-between items-center border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center">
            <Search className="h-5 w-5 mr-2 text-[#D72638]" />
            Ürün Ara
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition focus:outline-none rounded-full hover:bg-gray-100 p-1.5"
            aria-label="Kapat"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Arama Formu */}
        <form onSubmit={handleSearch} className="p-5 border-b border-gray-100">
          <div className="flex items-center w-full relative">
            <div className="relative flex-grow">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                <Search className="h-5 w-5" />
            </div>
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D72638] focus:border-[#D72638] transition-all text-gray-800 placeholder-gray-400"
                placeholder="Ne aramıştınız?"
              />
              {searchTerm && (
                <button
                  type="button"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setSearchTerm('')}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="ml-3 px-5 py-3.5 bg-[#D72638] hover:bg-[#C61D2D] text-white rounded-xl transition flex-shrink-0 shadow-md hover:shadow-lg font-medium"
            >
              Ara
            </button>
          </div>
        </form>

        {/* İçerik Alanı */}
        <div className="max-h-[60vh] overflow-y-auto">
          {/* Yükleme Durumu */}
          {isLoading && (
            <div className="flex justify-center items-center py-10">
              <Loader2 className="h-8 w-8 text-[#D72638] animate-spin" />
              <span className="ml-3 text-gray-600">Aranıyor...</span>
            </div>
          )}

          {/* Arama Sonuçları */}
          {!isLoading && searchTerm.trim() !== '' && (
            <>
            {searchResults.length > 0 ? (
                <div className="p-5 space-y-0.5">
                  <h3 className="text-sm font-medium text-gray-500 mb-2 px-1">Arama Sonuçları</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {searchResults.map((product) => (
                  <Link
                    key={product.id}
                    href={`/urunler/${getCategorySlugFromName(product.category || '') || 'diger'}/${product.slug}`}
                    onClick={onClose}
                        className="flex items-center p-3 hover:bg-gray-50 rounded-xl transition group border border-gray-100 hover:border-[#D72638]/20 hover:shadow-sm"
                  >
                        <div className="relative w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 border border-gray-200">
                      <Image
                            src={product.images[0]}
                        alt={product.name?.trim() || 'Filiz Kozmetik ürün görseli'}
                        fill
                            className="object-cover group-hover:scale-110 transition duration-300"
                        sizes="64px"
                      />
                          {product.discountPercentage && (
                            <div className="absolute top-0 right-0 bg-[#D72638] text-white text-[10px] font-bold px-1 rounded-bl-md">
                              %{product.discountPercentage}
                            </div>
                          )}
                    </div>
                        <div className="ml-3 flex-grow">
                          <h3 className="text-sm font-medium text-gray-800 group-hover:text-[#D72638] transition line-clamp-1">
                        {product.name}
                      </h3>
                          <p className="text-xs text-gray-500 mb-1 flex items-center">
                            <Tag className="h-3 w-3 mr-1 inline" />
                            {product.category}
                          </p>
                          <div className="flex items-center mt-1">
                             <span className="inline-block text-xs text-primary font-semibold">Ürünü İncele</span>
                             {typeof product.discountPercentage === 'number' && product.discountPercentage > 0 && (
                               <span className="inline-block bg-[#D72638] text-white text-[10px] font-bold px-2 py-0.5 rounded-full ml-2">
                                 %{product.discountPercentage} İndirim
                              </span>
                            )}
                    </div>
                    </div>
                        <ChevronRight className="h-5 w-5 text-gray-300 group-hover:text-[#D72638] transition" />
                  </Link>
                ))}
                  </div>
                  
                  <div className="text-center mt-5 pb-2">
                    <button
                      onClick={() => goToSearchResults(searchTerm)}
                      className="inline-flex items-center text-sm font-medium text-[#D72638] hover:text-[#C61D2D] px-4 py-2 rounded-md transition"
                    >
                      Tüm sonuçları gör
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-5 text-center py-10">
                  <div className="text-gray-300 mb-4">
                    <Search className="h-16 w-16 mx-auto" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-700 mb-2">Sonuç bulunamadı</h3>
                  <p className="text-gray-500 text-sm max-w-lg mx-auto">
                    "{searchTerm}" ile ilgili herhangi bir ürün bulunamadı. Lütfen başka anahtar kelimeler deneyin veya aşağıdaki popüler kategorilere göz atın.
                  </p>

                  <div className="grid grid-cols-2 gap-2 mt-6 max-w-md mx-auto">
                    {popularCategories.map((category) => (
                      <button
                        key={category.slug}
                        onClick={() => goToCategory(category.slug)}
                        className="bg-gray-50 hover:bg-gray-100 p-3 rounded-lg text-sm text-gray-700 hover:text-[#D72638] transition border border-gray-100 hover:border-[#D72638]/30 flex items-center justify-between"
                      >
                        <span>{category.name}</span>
                        <ChevronRight className="h-4 w-4 opacity-50" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
          
          {/* Başlangıç ekranı - arama terimi yokken */}
          {searchTerm.trim() === '' && (
            <div className="p-5">
              {/* Son aramalar */}
              {recentSearches.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-500">Son Aramalar</h3>
                    <button 
                      onClick={clearRecentSearches}
                      className="text-xs text-gray-400 hover:text-[#D72638]"
                    >
                      Temizle
                    </button>
                  </div>
                  <div className="space-y-1">
                    {recentSearches.map((term, index) => (
                      <button
                        key={index}
                        onClick={() => goToSearchResults(term)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 text-gray-700 flex items-center group"
                      >
                        <Search className="h-4 w-4 mr-2 text-gray-400 group-hover:text-[#D72638]" />
                        <span className="group-hover:text-[#D72638]">{term}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Popüler kategoriler */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-3">Popüler Kategoriler</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {popularCategories.map((category) => (
                    <button
                      key={category.slug}
                      onClick={() => goToCategory(category.slug)}
                      className="flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-xl text-gray-700 hover:text-[#D72638] transition border border-gray-100 hover:border-[#D72638]/30 group"
                    >
                      <span className="font-medium">{category.name}</span>
                      <ChevronRight className="h-5 w-5 text-gray-300 group-hover:text-[#D72638]" />
                    </button>
                  ))}
                </div>

                <div className="mt-6 bg-gradient-to-r from-[#3F0D12]/5 to-[#9B111E]/5 rounded-xl p-5">
                  <h3 className="text-base font-semibold text-gray-800 mb-2">Özel Tasarım Berber & Kuaför Mobilyaları</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Salonunuz için en uygun mobilya çözümlerini keşfedin. Tüm ürünlerimiz profesyonel kullanım için özel olarak tasarlanmıştır.
                  </p>
                  <Link
                    href="/urunler" 
                    onClick={onClose}
                    className="inline-flex items-center text-sm font-medium text-[#D72638] hover:text-[#C61D2D]"
                  >
                    Tüm ürünleri keşfet
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </Link>
                </div>
              </div>
              </div>
            )}
          </div>
      </div>
    </div>
  );
};

export default SearchModal; 